import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  ListItem,
  List,
  Stack,
  Pagination,
  PaginationItem,
  MenuItem,
  Select,
  FormControl,
  useTheme,
} from "@mui/material";
import { EmptyFields } from "../../../../../../utils/enums";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

export default function LibraryRiskItems({
  risks = [],
  selectedRisks = [],
  isRiskExisting = () => false,
  onToggle = () => {},
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
}) {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const paginatedRisks = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return risks?.slice(startIndex, startIndex + rowsPerPage);
  }, [page, rowsPerPage, risks]);

  const totalCount = risks?.length;
  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;

  function renderRiskItem(risk) {
    const isSelected = selectedRisks?.includes(risk?.key);
    const isExisting = isRiskExisting(risk);
    return (
      <ListItem
        key={risk?.id}
        sx={{
          border: `1px solid ${theme.customColors.border.main}`,
          borderRadius: "10px",
          mb: 1.5,
          background: isSelected
            ? theme.palette._blue.light
            : theme.customColors.background.main,
          boxShadow: theme.customColors.shadow.main,
          transition: "all 0.2s",
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 2,
          cursor: isExisting ? "default" : "pointer",
          minHeight: 60,
          borderColor: isSelected
            ? theme.palette._blue.main
            : theme.customColors.border.main,
          opacity: isExisting ? 0.7 : 1,
          "&:hover": {
            borderColor: isExisting
              ? theme.customColors.border.main
              : theme.palette._blue.main,
            backgroundColor: isExisting
              ? theme.customColors.background.main
              : theme.palette._blue.light,
          },
        }}
        onClick={() => !isExisting && onToggle(risk?.key)}
        disableGutters
      >
        <Checkbox
          checked={isSelected || isExisting}
          disabled={isExisting}
          onChange={() => !isExisting && onToggle(risk?.key)}
          onClick={(e) => e.stopPropagation()}
          sx={{ p: 0.5, mr: 2 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: theme.customColors.text.strong,
                lineHeight: 1.2,
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
              title={risk?.title}
            >
              {risk?.title || EmptyFields.TITLE}
            </Typography>
            {isExisting && (
              <Chip
                label="Already Added"
                size="small"
                sx={{
                  fontSize: "10px",
                  height: 18,
                  backgroundColor: theme.customColors.status.success.bg,
                  color: theme.customColors.status.success.text,
                  borderRadius: "8px",
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          <Typography
            sx={{
              fontSize: "12px",
              color: theme.customColors.text.secondary,
              mb: 0.5,
              lineHeight: 1.3,
              maxWidth: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              whiteSpace: "normal",
            }}
            title={risk?.description}
          >
            {risk?.description || EmptyFields.DESCRIPTION}
          </Typography>
        </Box>
        <Chip
          label={risk?.category_title}
          size="small"
          sx={{
            fontSize: "12px",
            height: 22,
            backgroundColor: theme.customColors.background.info,
            color: theme.palette._blue.main,
            fontWeight: 600,
            borderRadius: "12px",
            ml: 1,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        />
      </ListItem>
    );
  }

  return (
    <>
      <List sx={{ mt: 1, p: 0 }}>{paginatedRisks?.map(renderRiskItem)}</List>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ padding: 2 }}
      >
        <FormControl variant="outlined" size="small">
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(e.target.value);
              setPage(1);
            }}
          >
            {rowsPerPageOptions?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          variant="outlined"
          shape="rounded"
          renderItem={(item) => (
            <PaginationItem
              {...item}
              sx={{
                background: item.selected
                  ? `${theme.customColors.icons.dark} !important`
                  : "",
                color: item.selected
                  ? `${theme.customColors.button.text} !important`
                  : "",
              }}
            />
          )}
        />
      </Stack>
    </>
  );
}
